import { createStore } from 'redux';
import morphdom from 'morphdom';

const { twigWorker } = window.swinger.webworker;
const renderFingerprints = {};
const lastRenderedFingerprints = {};
const previouslyRendered = {};
const alreadyInitializedWidgets = new Set();
const alreadyProvisionedWidgets = new Set();
const focusableTextinputsSelector = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])',
  'textarea',
  '[contenteditable]',
].join(',');

const topLevelReducer = (widgetReducer) => (state, action) => {
  switch (action.type) {
    case 'REPLACE_STATE':
      state = action.value;
      break;

    default:
      return widgetReducer(state, action);
  }
  return state;
};

function refocusElem(elem) {
  if (document.activeElement === elem) {
    return;
  }

  if (!elem || !elem.isConnected) {
    return;
  }

  elem.focus();

  if (elem.value) {
    try {
      elem.setSelectionRange(elem.value.length, elem.value.length);
    } catch (e) {
      // noop
    }
    return;
  }

  if (elem.contentEditable) {
    const range = document.createRange();
    const sel = window.getSelection();

    if (elem.childNodes.length) {
      range.setStart(elem.childNodes[elem.childNodes.length - 1], elem.childNodes[elem.childNodes.length - 1].textContent.length);
    } else if (elem.textContent) {
      range.setStart(elem, elem.textContent.length);
    } else {
      return;
    }

    range.collapse(true);

    sel.removeAllRanges();
    sel.addRange(range);
  }
}

function insertHTML(html, id) {
  window.swinger.transition.execute(() => {
    window.swinger.transition.untagAll();

    const elem = window.swinger.$(`.swinger-widget[data-id="${id}"]`)[0];

    if (elem.childElementCount > 1) {
      window.console.error(`The "${id}" widget has more than one direct child element! Please ensure that the template used by that widget has a wrapper around all of the elements.`);
      return;
    }

    if (typeof html !== 'string') {
      window.console.error(`The Twig renderer for "${id}" did not return a string.`);
      return;
    }

    if (!html) {
      window.console.info(`The Twig renderer for "${id}" did return an empty/falsy string.`);
      return;
    }

    html = window.swinger.currency.reformatHTML(html);

    const targetNode = elem.firstElementChild
      ? elem.firstElementChild : elem.appendChild((() => {
        const newNode = document.createElement('div');

        newNode.classList.add('swinger-node');

        return newNode;
      })());

    if (!html) {
      elem.innerHTML = '';
    } else if (targetNode.innerHTML.trim()) {
      const focusedElement = document.activeElement;
      const focusedElementName = focusedElement.getAttribute('name');
      const focusedElementValue = focusedElement.value;
      const focusedElementId = focusedElement.id;

      morphdom(targetNode, html, {
        onBeforeElUpdated(fromEl, toEl) {
          if (
            fromEl === focusedElement
            && fromEl.matches(focusableTextinputsSelector)
          ) {
            return false;
          }

          // spec - https://dom.spec.whatwg.org/#concept-node-equals
          if (fromEl.isEqualNode(toEl)) {
            return false;
          }

          if (
            fromEl.matches('.swinger-slider')
            && !fromEl.matches('.swinger-mixed-results .swinger-slider')
            && toEl.matches('.swinger-slider')
            && !toEl.matches('.swinger-mixed-results .swinger-slider')
          ) {
            toEl.setAttribute('class', fromEl.getAttribute('class'));
          }

          if (
            fromEl.hasAttribute('data-fingerprint')
            && toEl.hasAttribute('data-fingerprint')
            && fromEl.getAttribute('data-fingerprint') === toEl.getAttribute('data-fingerprint')
          ) {
            return false;
          }

          if (
            fromEl.hasAttribute('srcset')
            && toEl.hasAttribute('srcset')
            && fromEl.getAttribute('srcset') === toEl.getAttribute('srcset')
          ) {
            return false;
          }

          [
            'open',
            'role',
            'aria-hidden',
            'aria-controls',
            'aria-expanded',
            'data-invoker',
            'data-track-content',
            'data-content-name',
            'data-content-piece',
          ].forEach((attribute) => {
            if (fromEl.hasAttribute(attribute)) {
              toEl.setAttribute(attribute, fromEl.getAttribute(attribute));
            }
          });

          return true;
        },
      });

      if (focusedElementId && document.getElementById(focusedElementId)) {
        if (focusedElementValue && document.getElementById(focusedElementId).matches(focusableTextinputsSelector)) {
          document.getElementById(focusedElementId).value = focusedElementValue;
        }

        refocusElem(document.getElementById(focusedElementId));
      } else if (focusedElementName && document.getElementsByName(focusedElementName).length) {
        const elemsByName = document.querySelectorAll(`[name="${focusedElementName}"]`);
        const elemByNameAndValue = document.querySelector(`[name="${focusedElementName}"][value="${focusedElementValue || 'xyz'}"]`);

        if (focusedElementValue && elemsByName.length > 1 && elemByNameAndValue) {
          refocusElem(elemByNameAndValue);
        } else {
          if (focusedElementValue && document.getElementsByName(focusedElementName)[0].matches(focusableTextinputsSelector)) {
            document.getElementsByName(focusedElementName)[0].value = focusedElementValue;
          }

          refocusElem(document.getElementsByName(focusedElementName)[0]);
        }
      }
    } else {
      elem.innerHTML = html;
    }
    console.info(`"${id}" widget inserted`);
    elem.classList.add('swinger-widget--rendered');
    window.swinger.eventHub.trigger(elem, 'swinger-widget:rendered');
  });
}

twigWorker.addEventListener('message', (e) => {
  const { id, fingerprint } = e.data;
  const html = e.data.html.trim();
  const elem = window.swinger.$(`.swinger-widget[data-id="${id}"]`)[0];

  previouslyRendered[id] = previouslyRendered[id] || {};
  previouslyRendered[id][fingerprint] = html;

  if (window.performance && window.performance.measure) {
    try {
      window.performance.mark(`TwigWorker ${id} ${fingerprint} end`);
      window.performance.measure(
        `TwigWorker ${id}`,
        `TwigWorker ${id} ${fingerprint} start`,
        `TwigWorker ${id} ${fingerprint} end`,
      );
    } catch (e) {
      // noop
    }
  }

  if (!elem || (elem.innerHTML && renderFingerprints[id] !== fingerprint)) {
    console.log(`${id}: Not handing off to MorphDOM`);
    return;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  if (
    template.content
    && template.content.firstElementChild
    && elem.firstElementChild
    && elem.firstElementChild.isEqualNode(template.content.firstElementChild)
  ) {
    console.log(`${id}: HTML is the same. Not handing off to DOM`);
    return;
  }

  console.log(`${id}: Handing off to DOM`);
  insertHTML(html, id);
});

function render(id, component, data, warmUpRender) {
  data.renderedByWidget = true;
  data.config = data.config || {};
  data.config = { ...window.swinger.config, ...data.config };

  let fingerprint = `${btoa(unescape(encodeURIComponent(JSON.stringify(data))))}`;

  renderFingerprints[id] = fingerprint;

  if (previouslyRendered[id] && previouslyRendered[id][fingerprint]) {
    insertHTML(previouslyRendered[id][fingerprint], id);
    return;
  }

  if (warmUpRender) {
    fingerprint += '-lazy';
  }

  if (lastRenderedFingerprints[id] === fingerprint) {
    return;
  }

  lastRenderedFingerprints[id] = fingerprint;

  if (window.performance && window.performance.measure) {
    window.performance.mark(`TwigWorker ${id} ${fingerprint} start`);
  }

  twigWorker.postMessage({
    action: 'render',
    id,
    component,
    data,
    fingerprint,
  });
}

window.swinger = {
  ...window.swinger,
  app: (params) => {
    window.swinger.observer.initialize(`.swinger-widget[data-type="${params.type}"]`, (elem) => {
      if (alreadyInitializedWidgets.has(elem)) {
        return;
      }
      alreadyInitializedWidgets.add(elem);

      const id = elem.getAttribute('data-id');
      const elemInitialState = window.swinger
        && window.swinger.widgets
        && window.swinger.widgets.states
        && window.swinger.widgets.states[id];

      const component = elem.getAttribute('data-component') || params.component;
      const initialState = {
        renderedByWidget: true,
        config: {},
        ...elemInitialState || {},
        ...params.initialState || {},
      };

      initialState.renderedByWidget = true;

      if (elem.classList.contains('swinger-widget--dynamic')) {
        render(
          id,
          component,
          initialState,
        );
      }

      window.swinger.eventHub.trigger(elem, 'swinger.widget:initialized');
    }, false);

    return {
      then: (callback) => {
        window.swinger.observer.initialize(`.swinger-widget[data-type="${params.type}"]`, (elem) => {
          if (alreadyProvisionedWidgets.has(elem)) {
            return;
          }
          alreadyProvisionedWidgets.add(elem);

          const id = elem.getAttribute('data-id');
          const proceed = () => {
            const elemInitialState = window.swinger
              && window.swinger.widgets
              && window.swinger.widgets.states
              && window.swinger.widgets.states[id];

            const initialState = {
              config: {},
              ...elemInitialState || {},
              ...params.initialState || {},
              renderedByWidget: true,
            };

            initialState.config.renderedByWidget = true;

            const bindEvent = (event, selector, handler) => {
              if (
                !window.swinger
                || !window.swinger.eventHub
                || !window.swinger.eventHub.register
              ) {
                window.setTimeout(() => {
                  bindEvent(event, selector, handler);
                }, 100);
                return;
              }

              if (typeof selector === 'function') {
                window.swinger.eventHub.register(event, `.swinger-widget[data-id="${id}"]`, selector);
              } else {
                const composedSelector = selector.split(',').map((selectorPart) => `.swinger-widget[data-id="${id}"] ${selectorPart}`).join(',');
                window.swinger.eventHub.register(event, composedSelector, handler);
              }
            };

            const component = elem.getAttribute('data-component') || params.component;
            const reducer = params.reducer || ((state) => state);
            const store = createStore(topLevelReducer(reducer), initialState);

            window.swinger = window.swinger || {};
            window.swinger.widgets = window.swinger.widgets || {};
            window.swinger.widgets.stores = window.swinger.widgets.stores || {};
            window.swinger.widgets.stores[id] = store;

            store.subscribe(() => {
              const state = store.getState();

              render(
                id,
                component,
                state,
              );

              window.swinger = window.swinger || {};
              window.swinger.widgets = window.swinger.widgets || {};
              window.swinger.widgets.states = window.swinger.widgets.states || {};
              window.swinger.widgets.states[id] = { ...window.swinger.widgets.states[id], ...initialState, ...state };
            });

            callback({
              store,
              bindEvent,
              elem,
              insertHTML,
            });
          };
          const waitForStateAndProceed = (id) => {
            if (!window.swinger.widgets.states[id]) {
              window.setTimeout(() => {
                waitForStateAndProceed(id);
              }, 50);

              return;
            }

            proceed();
          };

          if (!elem.hasAttribute('data-has-initialstate')) {
            proceed();
          }

          waitForStateAndProceed(id);
        }, false);

        return {
          catch: (callback) => { callback(); },
        };
      },
    };
  },
};



// WEBPACK FOOTER //
// ./src/widgets/vendor.js